import React, { useCallback, useMemo, useEffect } from 'react'
import { useIntl } from 'react-intl'
import BodyText from 'LuxKit/Typography/BodyText'
import Group from 'components/utils/Group'
import Heading from 'LuxKit/Typography/Heading'
import TileCard from 'tripPlanner/components/EntryPoints/Common/Tile/TileCard'
import LineBalanceScaleIcon from 'LuxKit/Icons/line/LineBalanceScaleIcon'
import LineCompassIcon from 'LuxKit/Icons/line/LineCompassIcon'
import LineWalkingIcon from 'LuxKit/Icons/line/LineWalkingIcon'
import VerticalSpacer from 'components/Common/Spacing/VerticalSpacer'
import TileCardsGroup from './Common/Tile/TileCardsGroup'
import { arrayToMap } from 'lib/array/arrayUtils'
import * as Analytics from 'analytics/analytics'

interface Props {
  proximity: Array<{
    proximity: string
    selected: boolean
  }>
  onProximitySelect: (proximity: string) => void
  onNext: () => void
  isGeneratingTrip: boolean
  stepNumber: number
  totalSteps: number
}

function AITripProximitySelection({
  proximity,
  onProximitySelect,
  onNext,
  isGeneratingTrip,
  stepNumber,
  totalSteps,
}: Props) {
  const intl = useIntl()
  const handleOnClick = useCallback((proximityValue: string) => {
    onProximitySelect(proximityValue)
    onNext()
  }, [onProximitySelect, onNext])

  const tripProximityMap = useMemo(() =>
    arrayToMap(proximity, proximityOption => proximityOption.proximity, proximityOption => proximityOption.selected),
  [proximity])

  useEffect(() => {
    Analytics.trackClientEvent({
      subject: 'travel_distance',
      action: 'view',
      category: 'trip_planner',
      type: 'nonInteraction',
    })
  }, [])

  const PROXIMITY = [
    {
      proximity: 'Keep it local',
      title: intl.formatMessage({
        id: 'tyFrml3MGD',
        defaultMessage: 'Keep it local',
        description: 'Title for local proximity option',
      }),
      description: intl.formatMessage({
        id: '/0MHSbNNmX',
        defaultMessage: 'Low travel time, high relaxation.',
        description: 'Description for local proximity option',
      }),
      icon: <LineWalkingIcon />,
    },
    {
      proximity: 'See the best within reach',
      title: intl.formatMessage({
        id: 'xvJH2Bc1R5',
        defaultMessage: 'Balanced',
        description: 'Title for balanced proximity option',
      }),
      description: intl.formatMessage({
        id: 'wJGZXZ1jBI',
        defaultMessage: 'The ideal mix of local and discovery.',
        description: 'Description for balanced proximity option',
      }),
      icon: <LineBalanceScaleIcon />,
    },
    {
      proximity: 'Keen to get out and about',
      title: intl.formatMessage({
        id: 'dwYr64qgG9',
        defaultMessage: 'Go all out',
        description: 'Title for adventurous proximity option',
      }),
      description: intl.formatMessage({
        id: 'NyCX291KVh',
        defaultMessage: 'Stretch the map, make it count.',
        description: 'Description for adventurous proximity option',
      }),
      icon: <LineCompassIcon />,
    },
  ]

  const stepText = intl.formatMessage(
    {
      id: 'ewX/UMLdx4',
      defaultMessage: '{stepNumber} of {totalSteps}',
      description: 'Step indicator showing current step number and total steps',
    },
    { stepNumber, totalSteps },
  )

  const headingText = intl.formatMessage(
    {
      id: 'Kd/Vdi0D5+',
      defaultMessage: 'Travel <i>Distance</i>',
      description: 'Heading text with italicized Distance',
    },
    {
      i: (chunks: React.ReactNode) => <i key="italic">{chunks}</i>,
    },
  )

  const descriptionText = intl.formatMessage({
    id: 's4cW9W1gES',
    defaultMessage: 'How far are you happy to venture from your hotel?',
    description: 'Question asking about preferred travel distance',
  })

  return (
    <Group direction="vertical" gap={20} desktopGap={24}>
      <VerticalSpacer gap={8}>
        <BodyText variant="large" weight="bold">{stepText}</BodyText>
        <Heading variant="heading1">
          {headingText}
        </Heading>
        <BodyText variant="large">
          {descriptionText}
        </BodyText>
      </VerticalSpacer>
      <TileCardsGroup>
        {PROXIMITY.map(proximityOption => (
          <TileCard
            key={proximityOption.proximity}
            title={proximityOption.title}
            subtitle={proximityOption.description}
            icon={proximityOption.icon}
            value={proximityOption.proximity}
            onClick={() => handleOnClick(proximityOption.proximity)}
            selected={!!tripProximityMap.get(proximityOption.proximity)}
            disabled={isGeneratingTrip}
          />))}
      </TileCardsGroup>
    </Group>
  )
}

export default AITripProximitySelection
